import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Textarea } from '../components/ui/textarea';
import api from '../utils/api';
import { toast } from 'sonner';
import { Building2, Mail, Lock, User, AlertCircle } from 'lucide-react';

const Register = ({ setAuth }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [showDomainExistsDialog, setShowDomainExistsDialog] = useState(false);
  const [showRequestAccessDialog, setShowRequestAccessDialog] = useState(false);
  const [showDifferentEmailDialog, setShowDifferentEmailDialog] = useState(false);
  const [accessRequestMessage, setAccessRequestMessage] = useState('');
  const [tenantInfo, setTenantInfo] = useState(null);
  const navigate = useNavigate();

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Step 1: Check if domain already has a tenant
      const domainCheck = await api.post('/auth/check-domain', { email });
      
      if (domainCheck.data.domain_exists) {
        // Domain already has a tenant - show dialog
        setTenantInfo(domainCheck.data);
        setShowDomainExistsDialog(true);
        setLoading(false);
        return;
      }
      
      // Step 2: Domain is new - proceed with registration
      const response = await api.post('/auth/register', { name, email, password });
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setAuth(true);
      toast.success('Account created successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestAccess = () => {
    setShowDomainExistsDialog(false);
    setShowRequestAccessDialog(true);
  };

  const handleUseDifferentEmail = () => {
    setShowDomainExistsDialog(false);
    setShowDifferentEmailDialog(true);
  };

  const submitAccessRequest = async () => {
    setLoading(true);
    try {
      await api.post('/auth/request-access', {
        email,
        name,
        message: accessRequestMessage
      });
      
      toast.success('Access request submitted successfully! The tenant admin will review your request.');
      setShowRequestAccessDialog(false);
      
      // Clear form
      setName('');
      setEmail('');
      setPassword('');
      setAccessRequestMessage('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit access request');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-4">
      <Card className="w-full max-w-md shadow-xl border-slate-200" data-testid="register-card">
        <CardHeader className="space-y-3 text-center">
          <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center shadow-lg">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-3xl font-bold bg-gradient-to-r from-slate-800 to-slate-600 bg-clip-text text-transparent">
            Create Account
          </CardTitle>
          <CardDescription className="text-base">Get started with your client management system</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleRegister} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-slate-700">Full Name</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="name"
                  data-testid="name-input"
                  type="text"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  className="pl-11 h-11 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  data-testid="email-input"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="pl-11 h-11 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  data-testid="password-input"
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={6}
                  className="pl-11 h-11 border-slate-300 focus:border-blue-500 focus:ring-blue-500"
                />
              </div>
            </div>
            <Button
              type="submit"
              data-testid="register-button"
              disabled={loading}
              className="w-full h-11 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-medium shadow-md"
            >
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="font-semibold text-blue-600 hover:text-blue-700">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Dialog: Domain Already Exists */}
      <AlertDialog open={showDomainExistsDialog} onOpenChange={setShowDomainExistsDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-amber-500" />
              Domain Already Has a Tenant
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                The email domain <strong>{tenantInfo?.domain}</strong> already has an existing tenant account.
              </p>
              {tenantInfo?.tenant_admin && (
                <p className="text-sm">
                  Tenant Admin: <strong>{tenantInfo.tenant_admin.name}</strong> ({tenantInfo.tenant_admin.email})
                </p>
              )}
              <p className="font-medium text-slate-700">
                Would you like to request access to join this tenant, or use a different email domain?
              </p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleUseDifferentEmail}>
              Use Different Email
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleRequestAccess} className="bg-blue-600 hover:bg-blue-700">
              Request Access
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog: Request Access Form */}
      <AlertDialog open={showRequestAccessDialog} onOpenChange={setShowRequestAccessDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Request Access to {tenantInfo?.domain}</AlertDialogTitle>
            <AlertDialogDescription>
              Send an access request to the tenant admin. They will review and approve your request.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="py-4 space-y-4">
            <div className="space-y-2">
              <Label htmlFor="access-message">Message (Optional)</Label>
              <Textarea
                id="access-message"
                placeholder="Explain why you need access to this tenant..."
                value={accessRequestMessage}
                onChange={(e) => setAccessRequestMessage(e.target.value)}
                className="min-h-[100px]"
              />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm">
              <p className="text-blue-800">
                <strong>Your details:</strong><br />
                Name: {name}<br />
                Email: {email}
              </p>
            </div>
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => {
              setShowRequestAccessDialog(false);
              setAccessRequestMessage('');
            }}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={submitAccessRequest} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? 'Submitting...' : 'Submit Request'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog: Use Different Email */}
      <AlertDialog open={showDifferentEmailDialog} onOpenChange={setShowDifferentEmailDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Use a Different Email Domain</AlertDialogTitle>
            <AlertDialogDescription>
              To create a new tenant, please use an email address from a different domain.
              The domain <strong>{tenantInfo?.domain}</strong> is already in use.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => {
              setShowDifferentEmailDialog(false);
              setEmail(''); // Clear email field
            }}>
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Register;
